// DS6.1.2
import React, { AriaAttributes, useEffect, useState } from 'react';
import c from 'classnames';
import ListCard from '../../List/ListCard';
import {
  ListItemButton,
  ListItemDiv,
  ListItemLink,
  ListItemToggle,
  type ListItemDivProps,
  type ListItemButtonProps,
  type ListItemLinkProps,
  type ListItemToggleProps,
} from '../../List/ListItem';
import { DropdownOption } from '../types';
import { isDropdownGroup } from '../helpers';
import omit from 'lodash/omit';
import { sanitizeHref } from '@wix/da-url';

import s from './MenuList.scss';

export type MenuItem = (
  | ListItemDivProps
  | ListItemButtonProps
  | ListItemLinkProps
  | Omit<ListItemToggleProps, 'variant'>
) &
  /* temporary for compatibility */ Pick<DropdownOption, 'link'>;

export interface NullableMenuItemGroup {
  label?: string;
  items: (MenuItem | false | null | undefined)[];
  className?: string;
  disabled?: boolean;
}

export type NullableMenuItemOrGroup =
  | MenuItem
  | NullableMenuItemGroup
  | false
  | null
  | undefined;

export interface Props {
  id: string; // for a11y
  items: NullableMenuItemOrGroup[];
  itemsFillContainerWidth?: boolean;
  wrappedInCard?: boolean;
  renderOnSSR?: boolean;
  className?: string;
  ariaLabel?: string;
  'aria-describedby'?: AriaAttributes['aria-describedby'];
  'aria-details'?: AriaAttributes['aria-details'];
}

const MenuList: React.FC<Props> = ({
  id,
  items,
  itemsFillContainerWidth = false,
  className,
  ariaLabel,
  'aria-describedby': ariaDescribedby,
  wrappedInCard = true,
  renderOnSSR = false,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Default is to render the menu on mount, set renderOnSSR
  const waitForMount =
    typeof window === 'undefined' && !mounted && renderOnSSR === false;

  if (waitForMount) {
    return null;
  }

  const renderListItem = (index, props: MenuItem) => {
    if (props.link || (props as ListItemLinkProps).href) {
      const href = props.link ?? (props as ListItemLinkProps).href;
      return (
        <ListItemLink
          key={index}
          href={sanitizeHref(href) as string}
          fillContainerWidth={itemsFillContainerWidth}
          {...(omit(props, ['newTag', 'link', 'href']) as Omit<
            ListItemLinkProps,
            'href'
          >)}
        />
      );
    }

    if (props.hasOwnProperty('onChange') && props.hasOwnProperty('checked')) {
      return (
        <ListItemToggle
          variant="main"
          {...(omit(props, ['newTag']) as Omit<ListItemToggleProps, 'variant'>)}
        />
      );
    }

    if ((props as ListItemButtonProps).onClick) {
      return (
        <ListItemButton
          key={index}
          fillContainerWidth={itemsFillContainerWidth}
          {...(omit(props, ['newTag']) as ListItemButtonProps)}
        />
      );
    }

    return (
      <ListItemDiv
        key={index}
        fillContainerWidth={itemsFillContainerWidth}
        {...(omit(props, ['newTag']) as ListItemDivProps)}
      />
    );
  };

  const renderList = () =>
    items.map((item, index) => {
      if (!item) {
        return null;
      }
      if (isDropdownGroup(item)) {
        return (
          <div key={index} className={s['group']}>
            {item.items.map(
              (elm: MenuItem, idx) => elm && renderListItem(idx, elm)
            )}
          </div>
        );
      } else {
        const singleItem = item as MenuItem;
        return renderListItem(index, singleItem);
      }
    });

  if (!wrappedInCard) {
    return <div className={c(s['root'], className)}>{renderList()}</div>;
  }

  return (
    <ListCard
      onClick={e => e.stopPropagation()}
      id={id}
      className={c(
        s['root'],
        !itemsFillContainerWidth && s['content-width'],
        className
      )}
      role="dialog"
      aria-describedby={ariaDescribedby}
      aria-label={ariaLabel}
      tabIndex={0}
    >
      {renderList()}
    </ListCard>
  );
};
MenuList.displayName = 'MenuList';

export default MenuList;
